import { AssessmentTabs } from "@/components/assessment/AssessmentTabs";
import { VulnerabilityRuleTable } from "@/components/assessment/methodology/VulnerabilityRuleTable";
import { RiskMatrixGrid } from "@/components/assessment/methodology/RiskMatrixGrid";
import { VulnerabilityByAssetTypeTable } from "@/components/assessment/methodology/VulnerabilityByAssetTypeTable";
import { HazardIndicatorTable } from "@/components/assessment/methodology/HazardIndicatorTable";
import { useAuthContext } from "@/context/AuthContext";

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="border-b border-border p-5">
        <h2 className="text-base font-bold leading-6">{title}</h2>
        {subtitle && <p className="mt-1 text-xs leading-5 text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

/**
 * Tab 5 — Methodology. Everything on this page is derived from the scoring
 * engine or the seeded reference data, never transcribed: the rule table and
 * the matrix call combineVulnerability()/scoreRisk() directly, and the
 * per-asset-type table reads ASSET_TYPE_VULNERABILITY_DEFAULTS. That is
 * deliberate — a hand-copied methodology page silently goes stale the first
 * time the engine changes, and this one is client-facing.
 */
function AssessmentMethodology() {
  const { workspace } = useAuthContext();
  const sector = workspace?.sector ?? "agriculture";

  return (
    <div className="flex flex-col gap-8">
      <AssessmentTabs />

      <div className="flex flex-col gap-0.5 border-b border-border pb-4">
        <h1 className="text-xl font-heading font-bold leading-7">How the scores are built</h1>
        <p className="text-xs leading-4 text-muted-foreground">
          Three numbers exist per asset. Two make the score. The third orders the results.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Panel
          title="Vulnerability"
          subtitle="Higher of the two reference scores, stepped up one level when both are equal at 2 or 3."
        >
          <VulnerabilityRuleTable />
        </Panel>

        <Panel title="Gross risk" subtitle="Exposure crossed with vulnerability — a lookup, not a multiplication.">
          <RiskMatrixGrid />
        </Panel>
      </div>

      <Panel
        title="Physical impact by asset type"
        subtitle="180Pi's reference score for the damage each hazard does to each asset type. This is one of the two inputs to vulnerability; the other is the business importance you set on Tab 1."
      >
        <VulnerabilityByAssetTypeTable sector={sector} />
      </Panel>

      <Panel title="Hazards and indicators" subtitle="What each hazard is measured with, and over which baseline period.">
        <HazardIndicatorTable />
      </Panel>
    </div>
  );
}

export default AssessmentMethodology;
